/**
 * Tabs atom — horizontal (default pill | line) and vertical (default fill | line accent).
 * Controlled only; panels/routing stay with the consumer.
 *
 * File split mirrors Button: types / size / color / styles / component.
 */

import React, { useCallback, useRef } from 'react';
import {
  getTabsCountClassName,
  getTabsDescriptionClassName,
  getTabsIconClassName,
  getTabsLabelClassName,
  getTabsListClassName,
  getTabsTriggerClassName,
} from './Tabs.styles';
import type { TabsProps } from './Tabs.types';

function enabledIndices(items: TabsProps['items']): number[] {
  return items.reduce<number[]>((acc, item, index) => {
    if (!item.disabled) acc.push(index);
    return acc;
  }, []);
}

export const Tabs: React.FC<TabsProps> = ({
  items,
  value,
  onValueChange,
  'aria-label': ariaLabel,
  orientation = 'horizontal',
  variant = 'default',
  size = 'md',
  color = 'primary',
  className,
}) => {
  const buttonRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const onValueChangeRef = useRef(onValueChange);
  onValueChangeRef.current = onValueChange;

  const focusAndSelect = useCallback(
    (index: number) => {
      const item = items[index];
      if (!item || item.disabled) return;
      buttonRefs.current[index]?.focus();
      if (item.id !== value) {
        onValueChangeRef.current(item.id);
      }
    },
    [items, value],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const enabled = enabledIndices(items);
      if (enabled.length === 0) return;

      const currentIndex = items.findIndex((item) => item.id === value);
      const position = Math.max(0, enabled.indexOf(currentIndex));
      const last = enabled.length - 1;

      const isHorizontal = orientation === 'horizontal';
      const nextKey = isHorizontal ? 'ArrowRight' : 'ArrowDown';
      const prevKey = isHorizontal ? 'ArrowLeft' : 'ArrowUp';

      let nextPosition: number | null = null;

      switch (event.key) {
        case nextKey:
          nextPosition = position >= last ? 0 : position + 1;
          break;
        case prevKey:
          nextPosition = position <= 0 ? last : position - 1;
          break;
        case 'Home':
          nextPosition = 0;
          break;
        case 'End':
          nextPosition = last;
          break;
        default:
          return;
      }

      event.preventDefault();
      focusAndSelect(enabled[nextPosition]);
    },
    [focusAndSelect, items, orientation, value],
  );

  const selectedIndex = items.findIndex((entry) => entry.id === value && !entry.disabled);
  const fallbackIndex = enabledIndices(items)[0] ?? -1;
  const activeTabIndex = selectedIndex >= 0 ? selectedIndex : fallbackIndex;

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      aria-orientation={orientation}
      data-slot="tabs-list"
      data-orientation={orientation}
      data-variant={variant}
      data-size={size}
      data-color={color}
      className={getTabsListClassName(orientation, variant, size, color, className)}
      onKeyDown={handleKeyDown}
    >
      {items.map((item, index) => {
        const isActive = item.id === value;
        const disabled = Boolean(item.disabled);
        const isVertical = orientation === 'vertical';

        return (
          <button
            key={item.id}
            ref={(node) => {
              buttonRefs.current[index] = node;
            }}
            type="button"
            role="tab"
            id={`tabs-trigger-${item.id}`}
            data-slot="tabs-trigger"
            data-active={isActive ? 'true' : undefined}
            data-tour={item.dataTour}
            title={item.title}
            aria-selected={isActive}
            aria-disabled={disabled || undefined}
            tabIndex={index === activeTabIndex ? 0 : -1}
            disabled={disabled}
            className={getTabsTriggerClassName(
              orientation,
              variant,
              size,
              color,
              isActive,
              disabled,
            )}
            onClick={() => {
              if (!disabled && item.id !== value) {
                onValueChangeRef.current(item.id);
              }
            }}
          >
            {item.icon ? (
              <span
                data-slot="tabs-icon"
                className={getTabsIconClassName(
                  orientation,
                  variant,
                  size,
                  color,
                  isActive,
                )}
              >
                {item.icon}
              </span>
            ) : null}

            {isVertical ? (
              <span data-slot="tabs-text" className="flex flex-col flex-1 min-w-0">
                <span
                  data-slot="tabs-label"
                  className={getTabsLabelClassName(orientation, variant, color, isActive)}
                >
                  {item.label}
                </span>
                {item.description != null && item.description !== '' ? (
                  <span
                    data-slot="tabs-description"
                    className={getTabsDescriptionClassName(color, variant, isActive)}
                  >
                    {item.description}
                  </span>
                ) : null}
              </span>
            ) : (
              <span
                data-slot="tabs-label"
                className={getTabsLabelClassName(orientation, variant, color, isActive)}
              >
                {item.label}
              </span>
            )}

            {item.count !== undefined ? (
              <span
                data-slot="tabs-count"
                className={getTabsCountClassName(size, color, isActive)}
              >
                {item.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
};

Tabs.displayName = 'Tabs';
